import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Quiz cevabı sonrası AI ile detaylı açıklama. Yanlış cevaplarda otomatik
 * ilgi çeker; doğru cevaplarda da "daha derin öğren" seçeneği. RAG +
 * Gemini backend'den.
 */
export default function AIExplainPanel({ questionId, selectedAnswer = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [opened, setOpened] = useState(false);

  const fetchExplanation = async () => {
    if (!questionId) return;
    setLoading(true);
    setOpened(true);
    try {
      const r = await fetch(`${API_URL}/api/quiz/explain`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question_id: String(questionId), selected_answer: selectedAnswer }),
      });
      if (r.status === 429) throw new Error('rate_limit');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      toast.error(
        e?.message === 'rate_limit'
          ? 'Çok fazla AI açıklama isteği. Lütfen 1 dakika bekleyin.'
          : 'AI açıklaması alınamadı'
      );
      setOpened(false);
    } finally {
      setLoading(false);
    }
  };

  if (!opened) {
    return (
      <Button
        size="sm" variant="outline"
        onClick={fetchExplanation}
        disabled={loading}
        className="gap-1.5 text-xs"
        data-testid="ai-explain-btn"
      >
        <Sparkles className="w-3.5 h-3.5" />
        {loading ? 'Açıklanıyor...' : 'Detaylı AI Açıklaması'}
      </Button>
    );
  }

  if (loading) {
    return (
      <div className="p-3 rounded-lg border border-primary/20 bg-primary/5 text-xs text-muted-foreground flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 animate-pulse text-primary" />
        AI açıklaması hazırlanıyor...
      </div>
    );
  }

  if (!data) return null;

  return (
    <div
      className="p-3 rounded-lg border border-primary/20 bg-primary/5 text-xs space-y-2"
      data-testid="ai-explain-panel"
    >
      <div className="flex items-center gap-2 text-primary font-medium">
        <Sparkles className="w-3.5 h-3.5" />
        AI Açıklaması
      </div>
      <div className="text-foreground/80 whitespace-pre-line leading-relaxed">
        {data.explanation}
      </div>
      {data.sources && data.sources.length > 0 && (
        <div className="pt-2 mt-2 border-t border-primary/10">
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-1">
            <BookOpen className="w-3 h-3" />Kaynaklar
          </div>
          <ul className="space-y-1">
            {data.sources.map((s, i) => s.snippet && (
              <li key={i} className="text-[10px] text-muted-foreground italic">
                <span className="font-medium text-foreground/60">{s.source || 'Mevzuat'}:</span> {s.snippet}…
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
